/**
 * Einstellungen (Stitch: maestro_einstellungen), Tab-Layout:
 * Betrieb (aus /api/me) · Team & Rollen (/api/members) · Module (Vorschau, B9/B10).
 */
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BUNDESLAENDER } from "@maestro/feiertage";
import { useApi, apiMutate } from "../hooks/useApi";
import { currentMembership } from "../lib/membership";
import type { Me, Member, Location } from "../ui/types";
import { ROLE_LABEL, formatDate, formatDateTime, parseGermanDecimal } from "../ui/format";
import { EmptyState, ErrorNote, Icon, LoadingRows, PageHeader } from "../ui/components";

const TABS = [
  { key: "betrieb", label: "Betrieb" },
  { key: "team", label: "Team & Rollen" },
  { key: "orte", label: "Orte" },
  { key: "zahlungen", label: "Zahlungen" },
  { key: "module", label: "Module" },
  { key: "branding", label: "Angebots-Vorlagen & Branding" },
  { key: "system", label: "System" },
  { key: "datenschutz", label: "Datenschutz" },
] as const;

/** H14: Icon-Ton je Modul-Kategorie statt einheitlich Terracotta (Buchhaltung/Zahlungen gruen,
 *  Kommunikations-Sync blau, E-Signatur accent, noch nicht angebundene Module neutral). */
type ModuleTone = "success" | "info" | "primary" | "neutral";
const TONE_CLASSES: Record<ModuleTone, string> = {
  success: "bg-[#E4F1E8] text-[#217A50] dark:bg-[#2E9963]/18 dark:text-[#7bc49a]",
  info: "bg-[#E7EEF7] text-[#31619B] dark:bg-[#31619B]/20 dark:text-[#9dbbe0]",
  primary: "bg-primary-fixed text-on-primary-fixed",
  neutral: "bg-surface-variant text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]",
};

/** M-35: gebaute Module haengen am ECHTEN Health-Flag (/api/system/health) statt pauschal
 *  "aktiv" auszusehen - healthKey verknuepft die Karte mit der Runtime-Konfiguration,
 *  missingText sagt ehrlich, was fehlt. planned=true bleibt "Geplant". */
const MODULES: { icon: string; name: string; desc: string; planned: boolean; statusText: string; tone: ModuleTone; healthKey?: "invoice" | "esign" | "ai" | "payments"; missingText?: string }[] = [
  { icon: "receipt_long", name: "LexOffice", desc: "Rechnungen automatisch erzeugen", planned: false, statusText: "Bereit · Rechnungen & Gutschriften", tone: "success", healthKey: "invoice", missingText: "Nicht konfiguriert · LexOffice-API-Key fehlt" },
  { icon: "mail", name: "E-Mail-Sync (IMAP)", desc: "Anfragen aus dem Postfach importieren", planned: true, statusText: "Geplant · Posteingang läuft aktuell über E-Mail-Routing", tone: "info" },
  { icon: "chat", name: "WhatsApp", desc: "Kundenkommunikation per WhatsApp", planned: true, statusText: "Geplant · noch nicht verbunden", tone: "neutral" },
  // CX-Audit 2026-07-17: Verweis korrigiert — die eSign-Template-ID liegt im Tab „Zahlungen"
  // (PaymentTermsForm), nicht unter „Angebots-Vorlagen & Branding".
  { icon: "signature", name: "E-Signatur", desc: "Verträge digital unterschreiben", planned: false, statusText: "Bereit · Kostenübernahme-Verträge", tone: "primary", healthKey: "esign", missingText: "Teilkonfiguriert · Composio-Key oder eSign-Template-ID fehlt (Tab „Zahlungen“)" },
  { icon: "auto_awesome", name: "KI-Assistent", desc: "Angebotstexte und Antworten vorschlagen", planned: false, statusText: "Bereit · Anschreiben, Freitext-Import, Chat", tone: "primary", healthKey: "ai", missingText: "Nicht konfiguriert · kein KI-Zugang hinterlegt" },
  { icon: "credit_card", name: "Stripe-Zahlungen", desc: "Online-Zahlung für Kunden", planned: false, statusText: "Bereit · Anzahlungs-Links", tone: "success", healthKey: "payments", missingText: "Nicht konfiguriert · Composio-Anbindung fehlt" },
];

/** Rollen-Badge-Stile im Stitch-Idiom (Inhaber/Admin = Terracotta, Mitarbeiter = Sekundär). */
const ROLE_PILL: Record<string, string> = {
  owner: "bg-primary/10 text-primary dark:bg-primary-fixed-dim/10 dark:text-primary-fixed-dim",
  admin: "bg-primary/10 text-primary dark:bg-primary-fixed-dim/10 dark:text-primary-fixed-dim",
  staff:
    "bg-secondary-container text-on-secondary-container dark:bg-surface-variant/30 dark:text-surface-variant",
};

/** Read-only-Feld im Formular-Look der Stitch-Vorlage (keine Speicher-Route vorhanden). */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
        {label}
      </label>
      <div className="rounded-lg border border-outline-variant bg-surface-container-low px-4 py-2.5 font-body-md text-body-md text-on-surface dark:border-outline/40 dark:bg-surface-variant/20 dark:text-inverse-on-surface">
        {children}
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const meQ = useApi<Me>("/api/me");
  // M-35: Module-Karten zeigen den ECHTEN Konfigurationsstand aus /api/system/health.
  const moduleHealthQ = useApi<SystemHealth>("/api/system/health");

  // M-27: Membership der aktuellen Subdomain, nicht die zufaellig erste (Multi-Tenant-Nutzer).
  const membership = currentMembership(meQ.data);
  const isOwner = membership?.role === "owner";
  const isOwnerOrAdmin = isOwner || membership?.role === "admin";
  const visibleTab = (key: string) =>
    (key !== "datenschutz" || isOwner) && (key !== "system" || isOwnerOrAdmin);

  // CX-Audit 2026-07-17: Tab in der URL (?tab=zahlungen …) statt nur im lokalen State —
  // vorher waren alle „siehe Tab X"-Hinweise tote Verweise und kein Bereich deep-linkbar
  // (mein-konto.tsx macht es bereits so). Reload/Zurueck erhalten den aktiven Tab.
  // Rollen-Guard: nicht sichtbare Tabs (Datenschutz/System) fallen auf „betrieb" zurueck.
  const [tabParams, setTabParams] = useSearchParams();
  const tabParam = tabParams.get("tab");
  const requestedTab: (typeof TABS)[number]["key"] = TABS.some((t) => t.key === tabParam)
    ? (tabParam as (typeof TABS)[number]["key"])
    : "betrieb";
  const tab = visibleTab(requestedTab) ? requestedTab : "betrieb";
  const setTab = (key: (typeof TABS)[number]["key"]) =>
    setTabParams(key === "betrieb" ? {} : { tab: key }, { replace: true });

  return (
    <>
      <PageHeader title="Einstellungen" subtitle="Betrieb, Team und Module verwalten." />

      {/* Tab-Navigation (Stitch: border-b, space-x-8) */}
      <div className="mb-8 tab-scroll flex gap-8 border-b border-outline-variant/30 dark:border-outline/30">
        {TABS.filter((t) => visibleTab(t.key)).map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`-mb-px whitespace-nowrap border-b-2 pb-4 font-label-md text-[12.5px] font-semibold transition-colors ${
              tab === t.key
                ? "border-primary text-primary dark:border-primary-fixed dark:text-primary-fixed"
                : "border-transparent text-on-surface-variant hover:text-primary dark:text-secondary-fixed-dim dark:hover:text-primary-fixed"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab: Betrieb */}
      {tab === "betrieb" && (
        <div className="grid grid-cols-1 gap-gutter lg:grid-cols-3">
          {/* Stammdaten */}
          <div className="card p-6 lg:col-span-2">
            <h3 className="mb-6 border-b border-outline-variant/30 pb-4 font-headline-sm text-headline-sm text-on-background dark:border-outline/30 dark:text-inverse-on-surface">
              Stammdaten
            </h3>
            {meQ.loading ? (
              <LoadingRows rows={2} />
            ) : membership ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <Field label="Unternehmensname">{membership.name}</Field>
                  <Field label="Subdomain">
                    <code className="font-mono">{membership.slug}</code>
                  </Field>
                </div>
                <div>
                  <label className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                    Ihre Rolle
                  </label>
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1 font-label-md text-label-md ${
                      ROLE_PILL[membership.role] ?? ROLE_PILL.staff
                    }`}
                  >
                    {ROLE_LABEL[membership.role] ?? membership.role}
                  </span>
                </div>
                <p className="border-t border-outline-variant/30 pt-4 font-body-sm text-body-sm text-secondary dark:border-outline/30 dark:text-secondary-fixed-dim">
                  Logo, Adresse (NAP) und Branding der Angebotsseite folgen mit dem
                  Onboarding-Modul (B11).
                </p>
              </div>
            ) : (
              <ErrorNote message={meQ.error ?? "Keine Mitgliedschaft gefunden."} />
            )}
          </div>

          {/* Zugang / Angemeldet als */}
          <div className="card flex flex-col p-6 lg:col-span-1">
            <h3 className="mb-6 border-b border-outline-variant/30 pb-4 font-headline-sm text-headline-sm text-on-background dark:border-outline/30 dark:text-inverse-on-surface">
              Konto
            </h3>
            <div className="flex flex-1 flex-col items-center justify-center rounded-xl border border-dashed border-outline-variant bg-surface-container-low p-6 text-center dark:border-outline/40 dark:bg-surface-variant/20">
              <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-fixed text-on-primary-fixed">
                <Icon name="badge" className="text-[28px]" />
              </span>
              <p className="font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                Angemeldet als
              </p>
              <p className="mt-1 break-all font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
                {meQ.data?.email ?? meQ.data?.userId ?? "–"}
              </p>
              {/* CX-Audit 2026-07-17: Sackgasse geschlossen — persönliche Einstellungen liegen
                  unter „Mein Konto", die Karte verweist jetzt dorthin. */}
              <Link to="/mein-konto" className="mt-3 font-label-md text-label-md text-primary hover:underline">
                Mein Konto öffnen
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Team & Rollen */}
      {tab === "team" && <TeamTab meUserId={meQ.data?.userId} isOwner={isOwner} isOwnerOrAdmin={isOwnerOrAdmin} />}

      {/* Tab: Orte (Locations-Stammdaten - Raeume/Kapazitaeten) */}
      {tab === "orte" && <LocationsTab />}

      {/* Tab: Module */}
      {tab === "module" && (
        <div className="grid grid-cols-1 gap-gutter md:grid-cols-2 xl:grid-cols-3">
          {MODULES.map((m) => {
            // M-35: "aktiv" heisst KONFIGURIERT (Health-Flag), nicht bloss "gebaut". Solange
            // Health laedt, lieber aus (kein falsches Gruen).
            const configured = m.healthKey ? moduleHealthQ.data?.integrations?.[m.healthKey] === true : !m.planned;
            const active = !m.planned && configured;
            const statusText = m.planned || configured ? m.statusText : (m.missingText ?? "Nicht konfiguriert");
            const isBeta = m.name === "KI-Assistent";
            return (
              <div key={m.name} className="card p-6 transition-shadow hover:shadow-md">
                <div className="mb-4 flex items-start justify-between">
                  {/* H14: Icon-Ton je Kategorie statt einheitlich Terracotta */}
                  <span className={`flex h-12 w-12 items-center justify-center rounded-lg ${TONE_CLASSES[m.tone]}`}>
                    <Icon name={m.icon} className="text-[22px]" />
                  </span>
                  {/* Toggle-Anmutung (Ein/Aus) — rein visuell, Modul-Aktivierung folgt */}
                  <span
                    aria-hidden
                    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                      active ? "bg-primary" : "bg-surface-variant dark:bg-surface-dim"
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                        active ? "translate-x-5" : "translate-x-0.5"
                      }`}
                    />
                  </span>
                </div>
                <div className="mb-2 flex items-center gap-2">
                  <h4 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">{m.name}</h4>
                  {/* H15: Beta-Badge blau */}
                  {isBeta && (
                    <span className="rounded-full bg-[#E7EEF7] px-1.5 py-0.5 font-label-md text-[9.5px] font-semibold uppercase tracking-wider text-[#31619B] dark:bg-[#31619B]/20 dark:text-[#9dbbe0]">
                      Beta
                    </span>
                  )}
                </div>
                <p className="mb-4 h-10 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                  {m.desc}
                </p>
                <div className="flex items-center gap-2 border-t border-outline-variant/30 pt-2.5 dark:border-outline/30">
                  <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${active ? "bg-tertiary" : "bg-secondary-fixed-dim"}`} />
                  <span className="min-w-0 flex-1 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">{statusText}</span>
                  {/* CX-Audit 2026-07-17: „Was fehlt" war nur Text ohne Weg dorthin — der
                      Konfigurations-Sprung führt jetzt direkt in den Zahlungen-Tab, wo die
                      Modul-Schalter/Keys liegen. */}
                  {!m.planned && !configured && m.healthKey && (
                    <button
                      type="button"
                      className="shrink-0 font-label-md text-[11px] font-semibold text-primary hover:underline"
                      onClick={() => setTab("zahlungen")}
                    >
                      Konfigurieren
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {/* H13: "Modul vorschlagen"-Karte */}
          <a
            href="mailto:info@monot.com?subject=Modul-Vorschlag%20MAESTRO"
            className="flex flex-col items-center justify-center gap-2 rounded-[13px] border-[1.5px] border-dashed border-outline-variant p-6 text-center text-secondary transition-colors hover:border-primary hover:text-primary dark:border-outline/40 dark:text-secondary-fixed-dim"
          >
            <span className="flex h-[34px] w-[34px] items-center justify-center rounded-lg bg-surface-variant dark:bg-white/[0.06]">
              <Icon name="add" className="text-[18px]" />
            </span>
            <span className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Modul vorschlagen</span>
            <span className="font-body-sm text-[11px] leading-relaxed">
              Welche Integration fehlt dir?
              <br />
              Sag es uns.
            </span>
          </a>
        </div>
      )}

      {/* Tab: Angebots-Vorlagen & Branding (H12) — Speicher-Route folgt (kein Feld im Backend). */}
      {tab === "branding" && (
        <div className="grid grid-cols-1 gap-gutter lg:grid-cols-3">
          <div className="card p-6 lg:col-span-2">
            <h3 className="mb-6 border-b border-outline-variant/30 pb-4 font-headline-sm text-headline-sm text-on-background dark:border-outline/30 dark:text-inverse-on-surface">
              Angebots-Vorlagen &amp; Branding
            </h3>
            <div className="space-y-6">
              <div>
                <label className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                  Akzentfarbe (öffentliche Angebotsseite)
                </label>
                <div className="flex items-center gap-3">
                  <span className="h-10 w-10 shrink-0 rounded-lg bg-primary shadow-sm" />
                  <span className="font-body-md text-body-md text-on-surface-variant dark:text-secondary-fixed-dim">Aktueller Accent-Ton der App</span>
                </div>
              </div>
              <div>
                <label className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                  Logo
                </label>
                <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-outline-variant bg-surface-container-low p-8 text-center text-secondary dark:border-outline/40 dark:bg-surface-variant/10 dark:text-secondary-fixed-dim">
                  <Icon name="image" className="text-[26px]" />
                  <span className="font-body-sm text-body-sm">Kein Logo hinterlegt</span>
                </div>
              </div>
              <p className="border-t border-outline-variant/30 pt-4 font-body-sm text-body-sm text-secondary dark:border-outline/30 dark:text-secondary-fixed-dim">
                Individuelle Akzentfarbe und Logo für die öffentliche Angebotsseite folgen mit dem Onboarding-Modul (B11) — aktuell nutzen alle Betriebe den App-Accent.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Zahlungen (Zahlungsbedingungen - Single Source für Angebot/Rechnung/Kostenübernahme) */}
      {tab === "zahlungen" && <PaymentTermsForm />}

      {/* Tab: System (Health + Error-Hub) - Owner/Admin */}
      {tab === "system" && isOwnerOrAdmin && <SystemTab />}

      {/* Tab: Datenschutz (DSGVO-Retention) - nur Inhaber */}
      {tab === "datenschutz" && isOwner && <RetentionTab />}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* System — Health (Integrations-Status) + Error-Hub (letzte Fehler).           */
/* -------------------------------------------------------------------------- */
interface SystemHealth {
  ok: boolean;
  integrations: Record<string, boolean>;
  errorsLast24h: number;
}
interface SystemError {
  id: string;
  path: string | null;
  method: string | null;
  statusCode: number | null;
  message: string | null;
  createdAt: string;
}
const INTEGRATION_LABEL: Record<string, string> = {
  ai: "KI (Angebot / Anschreiben / Import)",
  email: "E-Mail-Versand",
  esign: "E-Signatur (Kostenübernahme)",
  invoice: "LexOffice-Rechnung",
  payments: "Stripe-Zahlungen",
  places: "Google Places (Kunden-Lookup)",
};

interface Invite {
  id: string;
  email: string;
  role: "owner" | "admin" | "staff";
  status: "pending" | "accepted" | "revoked";
  createdAt: string;
  expiresAt: string;
}

/** Team & Rollen (Welle 4): einladen (Owner/Admin), Rolle aendern, entfernen. Schutz des letzten
 *  Owners + Owner/Admin-Gate laufen serverseitig (routes/invites.ts, routes/members.ts) - die UI
 *  zeigt nur die passenden Fehlermeldungen an. */
/** Leerer Formular-Zustand fuer Neuanlage/Bearbeitung einer Location. */
const EMPTY_LOCATION_FORM = {
  name: "",
  addressStreet: "",
  addressZip: "",
  addressCity: "",
  capacitySeated: "",
  capacityStanding: "",
  notes: "",
};

function LocationsTab() {
  const locationsQ = useApi<Location[]>("/api/locations");
  const locations = locationsQ.data ?? [];

  const [form, setForm] = useState(EMPTY_LOCATION_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);

  function startEdit(loc: Location) {
    setEditingId(loc.id);
    setForm({
      name: loc.name,
      addressStreet: loc.addressStreet ?? "",
      addressZip: loc.addressZip ?? "",
      addressCity: loc.addressCity ?? "",
      capacitySeated: loc.capacitySeated != null ? String(loc.capacitySeated) : "",
      capacityStanding: loc.capacityStanding != null ? String(loc.capacityStanding) : "",
      notes: loc.notes ?? "",
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(EMPTY_LOCATION_FORM);
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const payload = {
      name: form.name,
      addressStreet: form.addressStreet || undefined,
      addressZip: form.addressZip || undefined,
      addressCity: form.addressCity || undefined,
      capacitySeated: form.capacitySeated ? Number(form.capacitySeated) : undefined,
      capacityStanding: form.capacityStanding ? Number(form.capacityStanding) : undefined,
      notes: form.notes || undefined,
    };
    try {
      if (editingId) {
        await apiMutate(`/api/locations/${editingId}`, "PATCH", payload);
      } else {
        await apiMutate("/api/locations", "POST", payload);
      }
      cancelEdit();
      locationsQ.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(loc: Location) {
    setRowBusy(loc.id);
    setError(null);
    try {
      await apiMutate(`/api/locations/${loc.id}`, "PATCH", { isActive: !loc.isActive });
      locationsQ.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRowBusy(null);
    }
  }

  async function remove(loc: Location) {
    if (!confirm(`„${loc.name}" wirklich löschen? Verknüpfte Veranstaltungen verlieren nur die Ortszuordnung.`)) return;
    setRowBusy(loc.id);
    setError(null);
    try {
      await apiMutate(`/api/locations/${loc.id}`, "DELETE");
      if (editingId === loc.id) cancelEdit();
      locationsQ.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRowBusy(null);
    }
  }

  return (
    <div className="space-y-6">
      {error && <ErrorNote message={error} />}

      <div className="card p-6">
        <h3 className="mb-4 font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">
          {editingId ? "Ort bearbeiten" : "Ort anlegen"}
        </h3>
        <form className="grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={(e) => void submit(e)}>
          <label className="md:col-span-2">
            <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
              Name
            </span>
            <input
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="z. B. Festsaal, Terrasse, Weinkeller"
              className="input"
            />
          </label>
          <label>
            <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
              Straße
            </span>
            <input
              value={form.addressStreet}
              onChange={(e) => setForm((f) => ({ ...f, addressStreet: e.target.value }))}
              className="input"
            />
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label>
              <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                PLZ
              </span>
              <input
                value={form.addressZip}
                onChange={(e) => setForm((f) => ({ ...f, addressZip: e.target.value }))}
                className="input"
              />
            </label>
            <label>
              <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                Ort
              </span>
              <input
                value={form.addressCity}
                onChange={(e) => setForm((f) => ({ ...f, addressCity: e.target.value }))}
                className="input"
              />
            </label>
          </div>
          <label>
            <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
              Kapazität sitzend
            </span>
            <input
              type="number"
              min={0}
              value={form.capacitySeated}
              onChange={(e) => setForm((f) => ({ ...f, capacitySeated: e.target.value }))}
              className="input"
            />
          </label>
          <label>
            <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
              Kapazität stehend
            </span>
            <input
              type="number"
              min={0}
              value={form.capacityStanding}
              onChange={(e) => setForm((f) => ({ ...f, capacityStanding: e.target.value }))}
              className="input"
            />
          </label>
          <label className="md:col-span-2">
            <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
              Notizen
            </span>
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              className="input"
            />
          </label>
          <div className="flex items-center gap-3 md:col-span-2">
            <button type="submit" className="btn-primary !py-2" disabled={busy || !form.name}>
              {busy ? "Speichert …" : editingId ? "Speichern" : "Anlegen"}
            </button>
            {editingId && (
              <button type="button" className="font-label-md text-label-md text-secondary dark:text-secondary-fixed-dim" onClick={cancelEdit}>
                Abbrechen
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="card overflow-hidden">
        <div className="flex items-center justify-between border-b border-outline-variant/30 p-6 dark:border-outline/30">
          <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Orte</h3>
          {!locationsQ.loading && !locationsQ.error && locations.length > 0 && (
            <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              {locations.length} {locations.length === 1 ? "Ort" : "Orte"}
            </span>
          )}
        </div>

        {locationsQ.loading ? (
          <LoadingRows rows={3} />
        ) : locationsQ.error ? (
          <ErrorNote message={locationsQ.error} />
        ) : locations.length === 0 ? (
          <EmptyState icon="place" title="Noch keine Orte angelegt" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-left">
              <thead>
                <tr className="bg-surface-container-low dark:bg-surface-variant/20">
                  <th className="table-header">Name</th>
                  <th className="table-header">Adresse</th>
                  <th className="table-header">Kapazität</th>
                  <th className="table-header">Status</th>
                  <th className="table-header" />
                </tr>
              </thead>
              <tbody>
                {locations.map((loc) => (
                  <tr key={loc.id} className="transition-colors hover:bg-surface-container-high/20 dark:hover:bg-surface-variant/20">
                    <td className="border-t border-outline-variant/20 px-4 py-4 font-body-md text-body-md font-semibold text-on-background dark:border-outline/20 dark:text-inverse-on-surface">
                      {loc.name}
                    </td>
                    <td className="border-t border-outline-variant/20 px-4 py-4 font-body-md text-body-md text-on-surface-variant dark:border-outline/20 dark:text-secondary-fixed-dim">
                      {[loc.addressStreet, [loc.addressZip, loc.addressCity].filter(Boolean).join(" ")].filter(Boolean).join(", ") || "–"}
                    </td>
                    <td className="border-t border-outline-variant/20 px-4 py-4 font-body-md text-body-md text-on-surface-variant dark:border-outline/20 dark:text-secondary-fixed-dim">
                      {loc.capacitySeated != null || loc.capacityStanding != null
                        ? [
                            loc.capacitySeated != null ? `${loc.capacitySeated} sitzend` : null,
                            loc.capacityStanding != null ? `${loc.capacityStanding} stehend` : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")
                        : "–"}
                    </td>
                    <td className="border-t border-outline-variant/20 px-4 py-4">
                      <button
                        type="button"
                        disabled={rowBusy === loc.id}
                        onClick={() => void toggleActive(loc)}
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold disabled:opacity-50 ${
                          loc.isActive
                            ? "bg-tertiary-container text-tertiary"
                            : "bg-secondary-container text-on-secondary-container dark:bg-surface-variant/30 dark:text-surface-variant"
                        }`}
                      >
                        {loc.isActive ? "Aktiv" : "Inaktiv"}
                      </button>
                    </td>
                    <td className="border-t border-outline-variant/20 px-4 py-4 text-right dark:border-outline/20">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          type="button"
                          className="font-label-md text-label-md text-primary dark:text-primary-fixed-dim"
                          onClick={() => startEdit(loc)}
                        >
                          Bearbeiten
                        </button>
                        <button
                          type="button"
                          className="font-label-md text-label-md text-error disabled:opacity-50"
                          disabled={rowBusy === loc.id}
                          onClick={() => void remove(loc)}
                        >
                          Löschen
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function TeamTab({ meUserId, isOwner, isOwnerOrAdmin }: { meUserId?: string; isOwner: boolean; isOwnerOrAdmin: boolean }) {
  const membersQ = useApi<Member[]>("/api/members");
  const invitesQ = useApi<Invite[]>(isOwnerOrAdmin ? "/api/invites" : null);
  const members = membersQ.data ?? [];
  const pendingInvites = (invitesQ.data ?? []).filter((i) => i.status === "pending");

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"owner" | "admin" | "staff">("staff");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);

  async function invite(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await apiMutate("/api/invites", "POST", { email, role });
      setEmail("");
      setRole("staff");
      invitesQ.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    setRowBusy(id);
    setError(null);
    try {
      await apiMutate(`/api/invites/${id}`, "DELETE");
      invitesQ.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRowBusy(null);
    }
  }

  async function changeRole(stackUserId: string, newRole: string, oldRole: string) {
    // M-28: ein verrutschtes Dropdown darf niemanden zum Inhaber machen oder degradieren -
    // Bestaetigung mit alter/neuer Rolle VOR dem Request (Muster wie remove()). Bei Abbruch
    // Reload, damit das kontrollierte <select> wieder die tatsaechliche Rolle anzeigt.
    const label = (r: string) => ROLE_LABEL[r] ?? r;
    const warning = newRole === "owner"
      ? `Rolle wirklich von "${label(oldRole)}" zu "${label(newRole)}" aendern?\n\nAchtung: Inhaber haben volle Kontrolle ueber den Betrieb (Team, Daten, Loeschung).`
      : `Rolle wirklich von "${label(oldRole)}" zu "${label(newRole)}" aendern?`;
    if (!confirm(warning)) {
      membersQ.reload();
      return;
    }
    setRowBusy(stackUserId);
    setError(null);
    try {
      await apiMutate(`/api/members/${stackUserId}`, "PATCH", { role: newRole });
      membersQ.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRowBusy(null);
    }
  }

  async function remove(stackUserId: string) {
    if (!confirm("Mitglied wirklich entfernen?")) return;
    setRowBusy(stackUserId);
    setError(null);
    try {
      await apiMutate(`/api/members/${stackUserId}`, "DELETE");
      membersQ.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRowBusy(null);
    }
  }

  return (
    <div className="space-y-6">
      {error && <ErrorNote message={error} />}

      {isOwnerOrAdmin && (
        <div className="card p-6">
          <h3 className="mb-4 font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">
            Mitglied einladen
          </h3>
          <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => void invite(e)}>
            <label className="min-w-[220px] flex-1">
              <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                E-Mail
              </span>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@firma.de" className="input" />
            </label>
            <label>
              <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                Rolle
              </span>
              <select value={role} onChange={(e) => setRole(e.target.value as typeof role)} className="input">
                <option value="staff">Mitarbeiter</option>
                <option value="admin">Admin</option>
                {/* C-01: Owner-Ernennung bleibt Ownern vorbehalten (Server erzwingt das ebenfalls). */}
                {isOwner && <option value="owner">Inhaber</option>}
              </select>
            </label>
            <button type="submit" className="btn-primary !py-2" disabled={busy || !email}>
              {busy ? "Sendet …" : "Einladen"}
            </button>
          </form>

          {pendingInvites.length > 0 && (
            <div className="mt-6 space-y-2">
              {pendingInvites.map((i) => (
                <div key={i.id} className="flex items-center justify-between rounded-lg border border-outline-variant/40 p-3 dark:border-outline/30">
                  <div>
                    <p className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">{i.email}</p>
                    <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                      Eingeladen als {ROLE_LABEL[i.role] ?? i.role} · offen bis {formatDate(i.expiresAt)}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="font-label-md text-label-md text-error disabled:opacity-50"
                    disabled={rowBusy === i.id}
                    onClick={() => void revoke(i.id)}
                  >
                    Widerrufen
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="flex items-center justify-between border-b border-outline-variant/30 p-6 dark:border-outline/30">
          <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">
            Teammitglieder
          </h3>
          {!membersQ.loading && !membersQ.error && members.length > 0 && (
            <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              {members.length} {members.length === 1 ? "Mitglied" : "Mitglieder"}
            </span>
          )}
        </div>

        {membersQ.loading ? (
          <LoadingRows rows={3} />
        ) : membersQ.error ? (
          <ErrorNote message={membersQ.error} />
        ) : members.length === 0 ? (
          <EmptyState icon="group" title="Keine Teammitglieder sichtbar" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] border-collapse text-left">
              <thead>
                <tr className="bg-surface-container-low dark:bg-surface-variant/20">
                  <th className="table-header">Mitglied</th>
                  <th className="table-header">Rolle</th>
                  <th className="table-header">Beigetreten</th>
                  {isOwnerOrAdmin && <th className="table-header" />}
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr
                    key={m.stackUserId}
                    className="transition-colors hover:bg-surface-container-high/20 dark:hover:bg-surface-variant/20"
                  >
                    <td className="border-t border-outline-variant/20 px-4 py-4 dark:border-outline/20">
                      <div className="flex items-center gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-fixed text-on-primary-fixed">
                          <Icon name="person" className="text-[20px]" />
                        </span>
                        <p className="min-w-0 truncate font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
                          {m.stackUserId}
                          {m.stackUserId === meUserId ? " (Sie)" : ""}
                        </p>
                      </div>
                    </td>
                    <td className="border-t border-outline-variant/20 px-4 py-4 dark:border-outline/20">
                      {/* C-01: Admins duerfen Owner weder ernennen noch veraendern - Owner-Zeilen
                          sind fuer Nicht-Owner read-only, die Option "Inhaber" nur fuer Owner. */}
                      {isOwnerOrAdmin && (isOwner || m.role !== "owner") ? (
                        <select
                          value={m.role}
                          disabled={rowBusy === m.stackUserId}
                          onChange={(e) => void changeRole(m.stackUserId, e.target.value, m.role)}
                          className="input !w-auto !py-1"
                        >
                          <option value="staff">Mitarbeiter</option>
                          <option value="admin">Admin</option>
                          {isOwner && <option value="owner">Inhaber</option>}
                        </select>
                      ) : (
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            ROLE_PILL[m.role] ?? ROLE_PILL.staff
                          }`}
                        >
                          {ROLE_LABEL[m.role] ?? m.role}
                        </span>
                      )}
                    </td>
                    <td className="border-t border-outline-variant/20 px-4 py-4 font-body-md text-body-md text-on-surface-variant dark:border-outline/20 dark:text-secondary-fixed-dim">
                      {m.createdAt ? formatDate(m.createdAt) : "–"}
                    </td>
                    {isOwnerOrAdmin && (
                      <td className="border-t border-outline-variant/20 px-4 py-4 text-right dark:border-outline/20">
                        {(isOwner || m.role !== "owner") && (
                          <button
                            type="button"
                            className="font-label-md text-label-md text-error disabled:opacity-50"
                            disabled={rowBusy === m.stackUserId}
                            onClick={() => void remove(m.stackUserId)}
                          >
                            Entfernen
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SystemTab() {
  const healthQ = useApi<SystemHealth>("/api/system/health");
  const errorsQ = useApi<SystemError[]>("/api/system/errors");
  const health = healthQ.data;
  const errors = errorsQ.data ?? [];

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <h3 className="mb-4 font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface">
          Integrationen
        </h3>
        {healthQ.loading ? (
          <LoadingRows rows={3} />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {Object.entries(health?.integrations ?? {}).map(([key, active]) => (
              <div key={key} className="flex items-center gap-3 rounded-lg border border-outline-variant/40 p-3 dark:border-outline/30">
                <Icon
                  name={active ? "check_circle" : "cancel"}
                  className={`text-[20px] ${active ? "text-green-600 dark:text-green-400" : "text-secondary dark:text-secondary-fixed-dim"}`}
                />
                <div>
                  <p className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                    {INTEGRATION_LABEL[key] ?? key}
                  </p>
                  <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                    {active ? "Konfiguriert" : "Nicht konfiguriert"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface">
            Fehler
          </h3>
          {health && (
            <span className={`font-label-md text-label-md ${health.errorsLast24h > 0 ? "text-error" : "text-secondary dark:text-secondary-fixed-dim"}`}>
              {health.errorsLast24h} in den letzten 24 h
            </span>
          )}
        </div>
        {errorsQ.loading ? (
          <LoadingRows rows={3} />
        ) : errors.length === 0 ? (
          <EmptyState icon="check_circle" title="Keine Fehler" hint="Alles läuft rund." />
        ) : (
          <div className="space-y-2">
            {errors.map((e) => (
              <div key={e.id} className="rounded-lg border border-outline-variant/40 p-3 dark:border-outline/30">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono text-body-sm text-on-background dark:text-inverse-on-surface">
                    {e.method} {e.path}
                  </span>
                  <span className="shrink-0 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                    {formatDateTime(e.createdAt)}
                  </span>
                </div>
                {e.message && (
                  <p className="mt-1 break-words font-body-sm text-body-sm text-error">{e.message}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Datenschutz — DSGVO-Retention: Purge-Kandidaten (Dry-Run) + Anonymisierung.  */
/* -------------------------------------------------------------------------- */
interface RetentionCandidate {
  customerId: string;
  name: string;
  email: string;
  eventCount: number;
  retentionEndsAt: string;
  eligible: boolean;
}

function RetentionTab() {
  const { data, loading, reload } = useApi<RetentionCandidate[]>("/api/retention/candidates");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ purged: string[]; skipped: { customerId: string; reason: string }[]; emailRedactFailed?: string[] } | null>(null);

  const candidates = data ?? [];
  const eligible = candidates.filter((c) => c.eligible);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function purge() {
    if (selected.size === 0) return;
    const names = candidates.filter((c) => selected.has(c.customerId)).map((c) => c.name);
    if (
      !window.confirm(
        `${selected.size} Kunde(n) unwiderruflich anonymisieren?\n\n${names.join(", ")}\n\nEntfernt werden: Name, E-Mail, Telefon, Anschrift, Notizen sowie Kundendaten in Vorgängen (Freitexte, E-Mail-Verläufe, eSign-Unterzeichner, Angebots-Anschreiben). Beträge und Belege bleiben als Buchungsgrundlage erhalten. Kopien bei externen Anbietern (eSign, LexOffice, Mail-Provider) sind nicht erfasst.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await apiMutate<{ purged: string[]; skipped: { customerId: string; reason: string }[]; emailRedactFailed?: string[] }>(
        "/api/retention/purge",
        "POST",
        { customerIds: [...selected], confirm: true },
      );
      setResult(res);
      setSelected(new Set());
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card p-6">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface">
          DSGVO-Aufbewahrung
        </h3>
        {eligible.length > 0 && (
          <button type="button" className="btn-primary !py-2" disabled={selected.size === 0 || busy} onClick={() => void purge()}>
            <Icon name="delete_forever" className="text-[18px]" /> {busy ? "Wird anonymisiert …" : `${selected.size || ""} anonymisieren`.trim()}
          </button>
        )}
      </div>
      <p className="mb-6 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        Gewonnene Vorgänge (bezahlt/abgeschlossen) werden 10 Jahre aufbewahrt (§ 257 HGB), alle
        anderen 12 Monate ab der letzten Änderung. Anonymisieren entfernt Name, E-Mail, Telefon,
        Anschrift und Notizen des Kunden sowie Kundendaten in den Vorgängen (Freitexte,
        E-Mail-Verläufe, eSign-Unterzeichner, Angebots-Anschreiben) — Beträge und Belege bleiben
        als Buchungsgrundlage erhalten. Kopien bei externen Anbietern (eSign, LexOffice,
        Mail-Provider) sind nicht erfasst und müssen dort separat gelöscht werden.
      </p>
      {result && (
        <p className="mb-4 rounded-lg bg-green-100 px-4 py-3 font-body-sm text-body-sm text-green-800 dark:bg-green-900/30 dark:text-green-300">
          {result.purged.length} Kunde(n) anonymisiert{result.skipped.length > 0 ? `, ${result.skipped.length} übersprungen` : ""}
          {result.emailRedactFailed && result.emailRedactFailed.length > 0 ? ` — Achtung: E-Mail-Redaktion bei ${result.emailRedactFailed.length} Kunde(n) fehlgeschlagen (erneut ausführen).` : "."}
        </p>
      )}
      {error && <ErrorNote message={error} />}
      {loading ? (
        <LoadingRows rows={3} />
      ) : candidates.length === 0 ? (
        <EmptyState icon="privacy_tip" title="Keine Kunden vorhanden" />
      ) : (
        <div className="space-y-2">
          {candidates.map((c) => (
            <label
              key={c.customerId}
              className={`flex items-center gap-3 rounded-lg border p-3 ${
                c.eligible ? "border-outline-variant/40 dark:border-outline/30" : "border-outline-variant/20 opacity-60 dark:border-outline/15"
              }`}
            >
              <input type="checkbox" disabled={!c.eligible} checked={selected.has(c.customerId)} onChange={() => toggle(c.customerId)} />
              <div className="min-w-0 flex-1">
                <p className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                  {/* CX-Audit 2026-07-17: Vor dem Anonymisieren will man den Kunden prüfen —
                      der Name öffnet jetzt das Profil (vorher tote Anzeige der customerId-Daten). */}
                  <Link
                    to={`/kunden/${c.customerId}`}
                    onClick={(e) => e.stopPropagation()}
                    className="hover:text-primary hover:underline"
                  >
                    {c.name}
                  </Link>{" "}
                  <span className="font-normal text-secondary dark:text-secondary-fixed-dim">· {c.eventCount} Vorgang/Vorgänge</span>
                </p>
                <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{c.email}</p>
              </div>
              <span className={`shrink-0 font-label-md text-label-md ${c.eligible ? "text-green-700 dark:text-green-400" : "text-secondary dark:text-secondary-fixed-dim"}`}>
                {c.eligible ? "Löschbar seit " : "Aufbewahrung bis "}
                {formatDateTime(c.retentionEndsAt)}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Zahlungsbedingungen — eine Quelle für Angebot, Rechnung, Kostenübernahme.    */
/* -------------------------------------------------------------------------- */
interface PaymentTermsData {
  depositPercentBp?: number | null;
  // M-32: Fixbetrag sichtbar im Formular - ein unsichtbarer Altwert gewann sonst verdeckt
  // ueber die Prozentangabe (computeDepositCents priorisiert den Betrag).
  depositAmountCents?: number | null;
  paymentTermDays?: number | null;
  paymentTermText?: string | null;
  cancellationTerms?: string | null;
  finalPaymentMode?: "advance" | "onsite" | null;
  bankHolder?: string | null;
  bankName?: string | null;
  bankIban?: string | null;
  bankBic?: string | null;
  companyLegalName?: string | null;
  companyAddress?: string | null;
  companyRegister?: string | null;
  companyVatId?: string | null;
  companyPhone?: string | null;
  companyEmail?: string | null;
  companyPhotoUrl?: string | null;
  companyTagline?: string | null;
  esignTemplateId?: string | null;
  emailSenderName?: string | null;
  emailClosing?: string | null;
  emailSignature?: string | null;
  // Datenanreicherung Welle A (Migration 91): echte Schalter (nicht die kosmetische
  // MODULES-Vorschau) + Bundesland (editierbar, aber serverseitig aus companyAddress
  // vorbefuellt) + der geocodierte Cache (nur lesend, kein Eingabefeld).
  feiertageEnabled?: boolean;
  wetterEnabled?: boolean;
  bundesland?: string | null;
  companyLat?: number | null;
  companyLon?: number | null;
  geocodedAt?: string | null;
  // Datenanreicherung Welle B (Migration 92): Handelsregister + Bundesanzeiger + KI-Kurz-
  // zusammenfassung je Kunde mit gesetzter Firma.
  handelsregisterEnabled?: boolean;
  // Datenanreicherung Welle C (Migration 93): Google Places (New) — manueller Firmen-Lookup
  // je Kunde. Zusaetzlich gated durch system/health.integrations.places (GOOGLE_PLACES_API_KEY).
  googlePlacesEnabled?: boolean;
}

function TextField({ name, label, defaultValue, placeholder }: { name: string; label: string; defaultValue?: string | null; placeholder?: string }) {
  return (
    <label className="block">
      <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">{label}</span>
      <input name={name} defaultValue={defaultValue ?? ""} placeholder={placeholder} className="input" />
    </label>
  );
}

/** Kleine "Live"-Kennzeichnung: hebt echte, wirksame Schalter von der rein kosmetischen
 *  MODULES-Vorschau (Tab "Module") optisch ab. */
function LiveBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-tertiary-container px-2 py-0.5 font-label-md text-[10.5px] font-semibold uppercase tracking-wider text-tertiary">
      <span className="h-1.5 w-1.5 rounded-full bg-tertiary" />
      Live
    </span>
  );
}

/** Echter, wirksamer Ein/Aus-Schalter (im Gegensatz zur rein optischen Toggle-Anmutung in der
 *  MODULES-Karten-Anzeige) — Button mit role="switch", kein verstecktes Checkbox-Konstrukt noetig. */
function ToggleSwitch({
  checked,
  onChange,
  label,
  hint,
  disabled,
  disabledHint,
  disabledAction,
  children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint?: string;
  /** Ausgegraut + Klick wirkungslos — z.B. solange ein noetiges Secret fehlt (siehe
   *  Google-Places-Schalter unten: braucht GOOGLE_PLACES_API_KEY, system/health.integrations.places). */
  disabled?: boolean;
  /** Erklaerender Hinweis, NUR gerendert wenn disabled — z.B. "Braucht ... (siehe Tab System)". */
  disabledHint?: string;
  /** CX-Audit 2026-07-17: klickbares Sprungziel zum Hinweis (z.B. Link in den System-Tab) —
   *  vorher war „siehe Tab X" toter Text. NUR gerendert wenn disabled. */
  disabledAction?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className={`flex items-start justify-between gap-4 rounded-lg border border-outline-variant/40 p-4 dark:border-outline/30 ${disabled ? "opacity-60" : ""}`}>
      <div className="min-w-0 flex-1">
        <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">{label}</p>
        {hint && <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{hint}</p>}
        {disabled && disabledHint && (
          <p className="mt-1.5 flex items-center gap-1 font-body-sm text-body-sm text-error">
            <Icon name="lock" className="text-[15px]" /> {disabledHint}
          </p>
        )}
        {disabled && disabledAction}
        {children}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-disabled={disabled}
        disabled={disabled}
        title={disabled ? disabledHint : undefined}
        onClick={() => !disabled && onChange(!checked)}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
          checked ? "bg-primary" : "bg-surface-variant dark:bg-surface-dim"
        } ${disabled ? "cursor-not-allowed" : ""}`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </button>
    </div>
  );
}

function PaymentTermsForm() {
  const { data, loading } = useApi<PaymentTermsData | null>("/api/settings/payment-terms");
  // Datenanreicherung Welle C: braucht das system/health-Flag "places" (GOOGLE_PLACES_API_KEY
  // konfiguriert?), um den vierten Schalter unten ausgrauen zu koennen — Muster wie SystemTab.
  const healthQ = useApi<SystemHealth>("/api/system/health");
  const placesConfigured = healthQ.data?.integrations.places ?? false;
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  // Datenanreicherung Welle A: echte Schalter + Bundesland als kontrollierter State (die uebrigen
  // Felder bleiben unkontrolliert/defaultValue, wie im Rest dieses Formulars ueblich) -- diese
  // beiden brauchen kontrollierten State, da der Bundesland-Select nur bei aktivem
  // Feiertage-Schalter eingeblendet wird.
  const [feiertageEnabled, setFeiertageEnabled] = useState(false);
  const [wetterEnabled, setWetterEnabled] = useState(false);
  const [bundesland, setBundesland] = useState("");
  const [handelsregisterEnabled, setHandelsregisterEnabled] = useState(false);
  const [googlePlacesEnabled, setGooglePlacesEnabled] = useState(false);
  useEffect(() => {
    if (!loading) {
      setReady(true);
      if (data) {
        setFeiertageEnabled(data.feiertageEnabled ?? false);
        setWetterEnabled(data.wetterEnabled ?? false);
        setBundesland(data.bundesland ?? "");
        setHandelsregisterEnabled(data.handelsregisterEnabled ?? false);
        setGooglePlacesEnabled(data.googlePlacesEnabled ?? false);
      }
    }
  }, [loading, data]);

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const str = (k: string) => ((f.get(k) as string) || "").trim() || null;
    // M-O: „50,5" (deutsches Komma) darf nicht via Number() zu NaN -> null werden. Ungültige
    // Eingabe als Validierungsfehler melden statt die Anzahlung stillschweigend zu löschen.
    const depositRaw = ((f.get("depositPercent") as string | null) ?? "").trim();
    let depositPct: number | null = null;
    if (depositRaw !== "") {
      const pct = parseGermanDecimal(depositRaw);
      if (pct == null) {
        setError("Ungültiger Anzahlungs-Prozentsatz – bitte als Zahl eingeben, z. B. 30 oder 50,5.");
        return;
      }
      depositPct = Math.round(pct * 100);
    }
    // M-32: Fixbetrag-Feld (Euro, deutsches Komma erlaubt). Prozent und Fixbetrag schliessen
    // sich aus - das Backend lehnt beides gleichzeitig mit deposit_conflict ab; hier frueh
    // validieren, damit der Fehler vor dem Request sichtbar wird.
    const depositAmountRaw = ((f.get("depositAmount") as string | null) ?? "").trim();
    let depositAmountCents: number | null = null;
    if (depositAmountRaw !== "") {
      const amount = parseGermanDecimal(depositAmountRaw);
      if (amount == null) {
        setError("Ungültiger Anzahlungs-Fixbetrag – bitte als Zahl eingeben, z. B. 500 oder 499,50.");
        return;
      }
      depositAmountCents = Math.round(amount * 100);
    }
    if (depositPct != null && depositAmountCents != null) {
      setError("Anzahlung entweder als Prozentsatz ODER als Fixbetrag angeben – bitte eines der beiden Felder leeren.");
      return;
    }
    // M-33: Zahlungsziel Tage XOR Freitext - sonst divergieren Vertragstext (Freitext) und
    // berechnete Faelligkeit (Tage). Frueh validieren, Backend lehnt beides zusaetzlich ab.
    // A-10: "Zahlungsziel (Tage)" ist ein Text-Input (inputMode="numeric" verhindert am Desktop
    // weder Buchstaben noch Komma). Vorher wurde "5 Tage"/"7,5" via Number() zu NaN und beim
    // JSON.stringify still zu null -> das Zahlungsziel wurde unbemerkt geloescht. Jetzt hart
    // validieren: nur ganze, nicht-negative Tageszahlen.
    const paymentTermDaysRaw = ((f.get("paymentTermDays") as string | null) ?? "").trim();
    let paymentTermDays: number | null = null;
    if (paymentTermDaysRaw !== "") {
      const n = Number(paymentTermDaysRaw.replace(",", "."));
      if (!Number.isInteger(n) || n < 0) {
        setError("Ungültiges Zahlungsziel – bitte volle Tage als Zahl eingeben, z. B. 5.");
        return;
      }
      paymentTermDays = n;
    }
    const paymentTermText = str("paymentTermText");
    if (paymentTermDays != null && paymentTermText) {
      setError("Zahlungsziel entweder als Tage ODER als Freitext angeben – bitte eines der beiden Felder leeren.");
      return;
    }
    const finalPaymentModeRaw = f.get("finalPaymentMode") as string;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await apiMutate("/api/settings/payment-terms", "PUT", {
        depositPercentBp: depositPct,
        depositAmountCents, // M-32: immer mitsenden - null raeumt einen verdeckten Altwert ab
        paymentTermDays,
        paymentTermText,
        cancellationTerms: str("cancellationTerms"),
        finalPaymentMode: finalPaymentModeRaw === "advance" || finalPaymentModeRaw === "onsite" ? finalPaymentModeRaw : null,
        bankHolder: str("bankHolder"),
        bankName: str("bankName"),
        bankIban: str("bankIban"),
        bankBic: str("bankBic"),
        companyLegalName: str("companyLegalName"),
        companyAddress: str("companyAddress"),
        companyRegister: str("companyRegister"),
        companyVatId: str("companyVatId"),
        companyPhone: str("companyPhone"),
        companyEmail: str("companyEmail"),
        companyPhotoUrl: str("companyPhotoUrl"),
        companyTagline: str("companyTagline"),
        esignTemplateId: str("esignTemplateId"),
        emailSenderName: str("emailSenderName"),
        emailClosing: str("emailClosing"),
        emailSignature: str("emailSignature"),
        feiertageEnabled,
        wetterEnabled,
        bundesland: bundesland || null,
        handelsregisterEnabled,
        googlePlacesEnabled,
      });
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (!ready) return <LoadingRows />;
  const d = data ?? {};
  return (
    <form className="max-w-3xl space-y-8" onSubmit={(e) => void save(e)}>
      <p className="rounded-lg border border-outline-variant/40 bg-surface-container-low p-4 font-body-sm text-body-sm text-secondary dark:border-outline/30 dark:bg-surface-variant/10 dark:text-secondary-fixed-dim">
        Diese Angaben erscheinen <strong>identisch</strong> in Angebot, Rechnung und Kostenübernahme. Je Angebot lässt sich im Builder davon abweichen.
      </p>
      {error && <ErrorNote message={error} />}

      {/* Datenanreicherung Welle A: ECHTE Schalter (im Unterschied zur rein optischen
          MODULES-Vorschau im Tab "Module") -- klar abgesetzt, eigene Ueberschrift + "Live"-Badge. */}
      <section className="space-y-4 rounded-xl border-2 border-tertiary/30 bg-tertiary-container/10 p-5 dark:border-tertiary/25 dark:bg-tertiary-container/5">
        <div className="flex items-center gap-2">
          <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Datenanreicherung</h3>
          <LiveBadge />
        </div>
        <p className="-mt-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Diese Schalter wirken sofort nach dem Speichern (im Unterschied zur Vorschau unter „Module").
        </p>

        <ToggleSwitch
          checked={feiertageEnabled}
          onChange={setFeiertageEnabled}
          label="Feiertage im Kalender"
          hint="Bundesweite + regionale Feiertage als Badge im Kalender — rein clientseitig berechnet, kein externer Dienst."
        >
          {feiertageEnabled && (
            <label className="mt-3 block max-w-xs">
              <span className="mb-1 block font-body-sm text-[11px] font-semibold uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                Bundesland
              </span>
              <select value={bundesland} onChange={(e) => setBundesland(e.target.value)} className="input">
                <option value="">Nicht festgelegt</option>
                {BUNDESLAENDER.map((b) => (
                  <option key={b.code} value={b.code}>
                    {b.name}
                  </option>
                ))}
              </select>
              <span className="mt-1 block font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
                Wird beim Speichern der Firmenadresse (unten, „Firmen-Kopf") automatisch vorbefüllt — hier jederzeit überschreibbar.
              </span>
            </label>
          )}
        </ToggleSwitch>

        <ToggleSwitch
          checked={wetterEnabled}
          onChange={setWetterEnabled}
          label="Wetter-Widget je Veranstaltung"
          hint={'Kurze Wetterinfo (Bright Sky/DWD) im Vorgang — nutzt die Betriebsadresse bei „Im Haus"-Events bzw. die Kundenadresse bei „Außer Haus"/Catering (Ort-Typ je Vorgang unter „Bearbeiten").'}
        />

        <ToggleSwitch
          checked={handelsregisterEnabled}
          onChange={setHandelsregisterEnabled}
          label="Handelsregister-Check bei B2B-Kunden"
          hint="Sobald ein Kunde mit Firma angelegt wird (oder die Firma nachträglich eingetragen wird), läuft im Hintergrund einmalig ein kostenloser Abruf bei Handelsregister.de + Bundesanzeiger, dazu eine kurze KI-Zusammenfassung – sichtbar im Kunden-Profil. Handelsregister.de duldet informell ~60 Abrufe/Stunde insgesamt (über alle Mandanten hinweg gedeckelt); pro Firma wird nur einmal abgerufen."
        />

        <ToggleSwitch
          checked={googlePlacesEnabled}
          onChange={setGooglePlacesEnabled}
          label="Google-Places-Lookup im Kunden-Profil"
          hint='Manueller „Nachschlagen"-Button im Kunden-Profil: sucht Firma + Ort bei Google Places, zeigt Bewertung/Öffnungszeiten/Status sowie Firmenname/Telefon/Adresse zum gezielten Übernehmen – läuft NICHT automatisch bei neuen Kunden (jeder Abruf kostet, ein Mensch bestätigt jeden übernommenen Wert einzeln).'
          disabled={!placesConfigured}
          disabledHint='Braucht ein konfiguriertes GOOGLE_PLACES_API_KEY (Tab „System" → Integrationen).'
          disabledAction={
            /* CX-Audit 2026-07-17: „siehe Tab System" war toter Text — dank URL-Tabs jetzt Sprungziel. */
            <Link to="/einstellungen?tab=system" className="mt-1 inline-block font-label-md text-label-md text-primary hover:underline">
              Zum System-Tab
            </Link>
          }
        />
      </section>

      <section className="space-y-4">
        <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Zahlung</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Anzahlung (%)</span>
            <input name="depositPercent" inputMode="decimal" defaultValue={d.depositPercentBp != null ? (d.depositPercentBp / 100).toString() : ""} placeholder="50" className="input" />
          </label>
          {/* M-32: Fixbetrag sichtbar statt verdeckt - entweder Prozent ODER Fixbetrag. */}
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Anzahlung Fixbetrag (€)</span>
            <input name="depositAmount" inputMode="decimal" defaultValue={d.depositAmountCents != null ? (d.depositAmountCents / 100).toString().replace(".", ",") : ""} placeholder="500" className="input" />
            <span className="mt-1 block font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
              Alternative zum Prozentsatz – bitte nur eines der beiden Felder füllen.
            </span>
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Zahlungsziel (Tage)</span>
            <input name="paymentTermDays" inputMode="numeric" defaultValue={d.paymentTermDays ?? ""} placeholder="5" className="input" />
          </label>
        </div>
        <TextField name="paymentTermText" label="Zahlungsziel-Text (Alternative zu den Tagen – bitte nur eines der beiden Felder füllen)" defaultValue={d.paymentTermText} placeholder="5 Werktage nach Rechnungserhalt" />
        <label className="block">
          <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Restzahlung (nach der Anzahlung)</span>
          <select name="finalPaymentMode" defaultValue={d.finalPaymentMode ?? ""} className="input">
            <option value="">Nicht festgelegt</option>
            <option value="advance">Vorab (Überweisung/Link vor der Veranstaltung)</option>
            <option value="onsite">Vor Ort (am Veranstaltungstag)</option>
          </select>
          <span className="mt-1 block font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
            Erscheint auf der Anzahlungsrechnung, damit Gäste von Anfang an wissen, wie der offene Rest beglichen wird.
          </span>
        </label>
        <TextField name="cancellationTerms" label="Stornobedingungen (optional)" defaultValue={d.cancellationTerms} />
      </section>

      <section className="space-y-4">
        <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Kontoverbindung</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField name="bankHolder" label="Kontoinhaber" defaultValue={d.bankHolder} />
          <TextField name="bankName" label="Bank" defaultValue={d.bankName} />
          <TextField name="bankIban" label="IBAN" defaultValue={d.bankIban} />
          <TextField name="bankBic" label="BIC" defaultValue={d.bankBic} />
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">eSign (Kostenübernahme B2B)</h3>
        <TextField name="esignTemplateId" label="eSignatures.io Template-ID" defaultValue={d.esignTemplateId} placeholder="0f9f9ad4-02a8-4678-889a-52d3b4bd459e" />
        <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Ohne Template-ID kann keine Kostenübernahme angefordert werden ("Zahlungen → Kostenübernahme" im Vorgang).
        </p>
      </section>

      <section className="space-y-4">
        <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Firmen-Kopf (Kostenübernahme / Rechnung / E-Mail-Footer)</h3>
        <TextField name="companyLegalName" label="Firmenname (rechtlich)" defaultValue={d.companyLegalName} placeholder="Musterhof Catering GmbH" />
        <TextField name="companyAddress" label="Adresse" defaultValue={d.companyAddress} placeholder="Musterstraße 12 · 10115 Berlin" />
        {/* Datenanreicherung Welle A: rein informativ -- die editierbare Auswahl ist oben im
            "Datenanreicherung"-Abschnitt. */}
        {d.companyAddress ? (
          d.companyLat != null && d.bundesland ? (
            <p className="-mt-2 font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
              Erkanntes Bundesland: {BUNDESLAENDER.find((b) => b.code === d.bundesland)?.name ?? d.bundesland} (automatisch aus der Adresse ermittelt).
            </p>
          ) : (
            <p className="-mt-2 font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
              Bundesland wird beim Speichern automatisch anhand dieser Adresse ermittelt.
            </p>
          )
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField name="companyPhone" label="Telefon" defaultValue={d.companyPhone} placeholder="+49 30 12345678" />
          <TextField name="companyEmail" label="E-Mail" defaultValue={d.companyEmail} placeholder="info@musterhof-catering.de" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField name="companyRegister" label="Registereintrag" defaultValue={d.companyRegister} placeholder="Amtsgericht Berlin HRB 123456" />
          <TextField name="companyVatId" label="USt-IdNr." defaultValue={d.companyVatId} placeholder="DE123456789" />
        </div>
        <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Adresse, Telefon, E-Mail, Registereintrag und USt-IdNr. erscheinen (nur soweit ausgefüllt) im Footer jeder Angebots-/Nachfass-Mail.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField name="companyPhotoUrl" label="Foto-URL (Hero-Band in der Angebots-Mail)" defaultValue={d.companyPhotoUrl} placeholder="https://…/foto.jpg" />
          <TextField name="companyTagline" label="Tagline" defaultValue={d.companyTagline} placeholder="Events & Catering · Berlin" />
        </div>
        <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Ohne Foto-URL bleibt die Angebots-Mail wie bisher (kein Foto-Band).
        </p>
      </section>

      <section className="space-y-4">
        <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">E-Mail-Signatur</h3>
        <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Grußformel + Signatur erscheinen am Ende von Angebots- und Nachfass-Mails. Ohne Eintrag: Standard-Grußformel + Absendername.
        </p>
        <TextField name="emailSenderName" label="Absendername" defaultValue={d.emailSenderName} placeholder="Musterhof Catering" />
        <TextField name="emailClosing" label="Grußformel" defaultValue={d.emailClosing} placeholder="Mit freundlichen Grüßen" />
        <label className="block">
          <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Signatur (mehrzeilig, optional)</span>
          <textarea
            name="emailSignature"
            rows={5}
            defaultValue={d.emailSignature ?? ""}
            placeholder={"Max Mustermann\nMusterhof Catering\nTel. 030 12345678 · www.musterhof-catering.de"}
            className="input resize-y"
          />
        </label>
      </section>

      <div className="flex items-center gap-3">
        <button type="submit" className="btn-primary" disabled={busy}>{busy ? "Speichert …" : "Speichern"}</button>
        {saved && <span className="inline-flex items-center gap-1 font-body-sm text-body-sm text-primary"><Icon name="check_circle" className="text-[18px]" /> Gespeichert</span>}
      </div>
    </form>
  );
}
